import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const ReissuePassport = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const generateApplicationId = () =>
    Math.floor(100000 + Math.random() * 900000).toString();
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    profession: "",
    policeStation: "",
    contactNo: "",
    emergencyContact: "",
    email: "",
    education: "",
    aadhaarCard: null,
    panCard: null,
    anyDocument: null,
    birthCertificate: null,
    passportPhotoFront: null,
    passportPhotoBack: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Generate application ID
    const applicationId = generateApplicationId();

    // Prepare one combined object with all form data (including files as file names)
    const combinedData = {
      applicationId,
      name: formData.name,
      profession: formData.profession,
      policeStation: formData.policeStation,
      contactNo: formData.contactNo,
      emergencyContact: formData.emergencyContact,
      email: formData.email,
      education: formData.education,
      userEmail: user?.email || "Not logged in",
      // For files, just show the file name or null if not selected
      aadhaarCard: formData.aadhaarCard ? formData.aadhaarCard.name : null,
      panCard: formData.panCard ? formData.panCard.name : null,
      anyDocument: formData.anyDocument ? formData.anyDocument.name : null,
      birthCertificate: formData.birthCertificate
        ? formData.birthCertificate.name
        : null,
      passportPhotoFront: formData.passportPhotoFront
        ? formData.passportPhotoFront.name
        : null,
      passportPhotoBack: formData.passportPhotoBack
        ? formData.passportPhotoBack.name
        : null,
    };

    // Show combined object in console
    console.log("📦 Full Submitted Data: ", combinedData);

    // Create FormData to submit (files cannot be stringified, so keep them separate)
    const dataToSend = new FormData();
    dataToSend.append("applicationData", JSON.stringify(combinedData));

    // Append actual files for upload
    if (formData.aadhaarCard)
      dataToSend.append("aadhaarCard", formData.aadhaarCard);
    if (formData.panCard) dataToSend.append("panCard", formData.panCard);
    if (formData.anyDocument)
      dataToSend.append("anyDocument", formData.anyDocument);
    if (formData.birthCertificate)
      dataToSend.append("birthCertificate", formData.birthCertificate);
    if (formData.passportPhotoFront)
      dataToSend.append("passportPhotoFront", formData.passportPhotoFront);
    if (formData.passportPhotoBack)
      dataToSend.append("passportPhotoBack", formData.passportPhotoBack);

    try {
      const res = await axios.post(
        "http://localhost:5000/api/reissue-passport",
        dataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ Submitted:", res.data);
      alert(`Application submitted! ID: ${res.data.applicationId}`);
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      alert("Failed to submit form. Please try again.");
    }
  };

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 2));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Progress percentage (2 steps)
  const progressPercent = (step / 2) * 100;

  return (
    <Container>
      <StepIndicator>
        {["Personal Info", "Contact & Upload Documents"].map((label, index) => {
          const current = index + 1;
          const isActive = step === current;
          const isCompleted = step > current;

          return (
            <Step
              key={index}
              className={isActive ? "active" : isCompleted ? "completed" : ""}
            >
              <StepCircle>{current}</StepCircle>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </StepIndicator>

      {/* Progress Bar */}
      <ProgressBarContainer>
        <ProgressFill style={{ width: `${progressPercent}%` }} />
      </ProgressBarContainer>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        {step === 1 && (
          <Section $delay="0s">
            <h2>Step 1: Personal Information</h2>
            <Label>Full Name</Label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoComplete="name"
            />

            <Label>Profession</Label>
            <Input
              name="profession"
              value={formData.profession}
              onChange={handleChange}
              required
            />

            <Label>Near Police Station Name</Label>
            <Input
              name="policeStation"
              value={formData.policeStation}
              onChange={handleChange}
              required
            />
          </Section>
        )}

        {step === 2 && (
          <MainForm>
            <Section $delay="0s">
              <h2>Step 2: Contact & Upload Documents</h2>

              <Label>Contact No</Label>
              <Input
                name="contactNo"
                value={formData.contactNo}
                onChange={handleChange}
                required
                type="tel"
              />

              <Label>Emergency Contact</Label>
              <Input
                name="emergencyContact"
                value={formData.emergencyContact}
                onChange={handleChange}
                required
                type="tel"
              />

              <Label>Email</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <Label>Education</Label>
              <Input
                name="education"
                value={formData.education}
                onChange={handleChange}
                required
              />

              <Label>Aadhaar Card</Label>
              <Input
                type="file"
                name="aadhaarCard"
                onChange={handleChange}
                required
                accept=".jpg,.jpeg,.png,.pdf"
              />

              <Label>PAN Card</Label>
              <Input
                type="file"
                name="panCard"
                onChange={handleChange}
                required
                accept=".jpg,.jpeg,.png,.pdf"
              />

              <Label>Any Document (TC, Bank Passbook)</Label>
              <Input
                type="file"
                name="anyDocument"
                onChange={handleChange}
                accept=".jpg,.jpeg,.png,.pdf"
              />

              <Label>Birth Certificate (if child)</Label>
              <Input
                type="file"
                name="birthCertificate"
                onChange={handleChange}
                accept=".jpg,.jpeg,.png,.pdf"
              />

              <Label>Passport Photo (Front)</Label>
              <Input
                type="file"
                name="passportPhotoFront"
                onChange={handleChange}
                required
                accept=".jpg,.jpeg,.png"
              />

              <Label>Passport Photo (Back)</Label>
              <Input
                type="file"
                name="passportPhotoBack"
                onChange={handleChange}
                required
                accept=".jpg,.jpeg,.png"
              />
            </Section>
          </MainForm>
        )}

        <ButtonRow>
          {step > 1 && (
            <Button type="button" onClick={prevStep}>
              Previous
            </Button>
          )}
          {step < 2 && (
            <Button type="button" onClick={nextStep}>
              Next
            </Button>
          )}
          {step === 2 && <Button type="submit">Submit</Button>}
        </ButtonRow>
      </form>
    </Container>
  );
};

export default ReissuePassport;

// ---------------------- Styled Components ----------------------

const StepCircle = styled.div`
  width: 30px;
  height: 30px;
  background-color: #dee2e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
`;

const StepLabel = styled.span`
  margin-top: 0.5rem;
  font-size: 1.1rem;
  color: #e6e6e6;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  text-align: center;
  min-width: 100px;

  &::after {
    content: "";
    position: absolute;
    top: 15px;
    left: 50%;
    width: 100%;
    height: 4px;
    background-color: #ccc;
    z-index: -1;
    transform: translateX(-50%);
  }

  &:last-child::after {
    display: none;
  }

  &.active ${StepCircle} {
    background-color: #194bfd;
    color: white;
  }

  &.completed::after {
    background-color: #194bfd;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem; /* less margin to fit progress bar */
  padding: 0 10px;
  flex-wrap: wrap;
`;

// New progress bar container
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background-color: #444;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

// The colored fill of progress bar
const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(25, 75, 253, 1) 0%,
    rgba(173, 19, 251, 1) 100%
  );
  transition: width 0.4s ease-in-out;
`;

const Container = styled.div`
  max-width: 700px;
  background: linear-gradient(145deg, #0d0d0d, #1a1a1a);
  margin: 0 auto;
  margin-bottom: 100px;
  padding: 2rem;
  border: 1px solid #353535;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

const MainForm = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Section = styled.div`
  flex: 1;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${(props) => props.$delay || "0s"};

  h2 {
    color: #e6e6e6;
  }
`;

const Label = styled.label`
  display: block;
  text-align: start;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  color: #e6e6e6;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  border: 1px solid #353535;
  background-color: transparent;
  color: #e6e6e6;
  border-radius: 4px;

  &:focus {
    border-color: #3f51ff;
    outline: none;
  }
`;

const Button = styled.button`
  background: linear-gradient(
    90deg,
    rgba(25, 75, 253, 1) 0%,
    rgba(173, 19, 251, 1) 100%
  );
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  margin-right: 1rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #5a189a;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(90, 24, 154, 0.5);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;
